import React, { useState } from 'react';

/* ═══════════════════════════════════════════════════════════════
   Treas. Reg. §1.148-5 Purpose-Investment Yield Calculator

   Solves for the IRR r such that NPV(cash_flows, r) = 0 under the
   selected compounding frequency and day-count convention, using
   the same bisection approach as the existing Bond Yield Calculator
   (YieldCalculator.js). The solver is intentionally isolated from
   the classification layer; all rules about which fees enter the
   cash-flow stream run BEFORE the solver is called.

   Classification rules implemented here (per §1.148-5(e)):
   - Every fee must be classified: included in yield, OR excluded
     as a qualified administrative cost ("QAC").
   - Excluding a fee requires a 7-item QAC checklist.
   - Failing checklist items 3, 4, or 5 auto-flags the fee as
     likely included in yield; an explicit user override is
     required to exclude it, with a warning displayed.
   - Upfront included fees are treated as receipts at t=0
     (mathematically equivalent to reducing the initial advance).
   - Custom-schedule included fees are treated as receipts on
     their payment dates.

   Yield restriction test:
   - User selects a permissible spread (program = 1.50%,
     non-program purpose = 0.125%, custom = user-entered).
   - max_permitted_yield = bond_yield + permissible_spread
   - PASS if investment_yield ≤ max_permitted_yield, FAIL otherwise.
   ═══════════════════════════════════════════════════════════════ */

// ── Day count helpers ───────────────────────────────────────
function days30360(d1, d2) {
  let y1 = d1.getFullYear(), m1 = d1.getMonth() + 1, d1d = d1.getDate();
  let y2 = d2.getFullYear(), m2 = d2.getMonth() + 1, d2d = d2.getDate();
  if (d1d === 31) d1d = 30;
  if (d2d === 31 && d1d === 30) d2d = 30;
  return 360 * (y2 - y1) + 30 * (m2 - m1) + (d2d - d1d);
}

function daysActual(d1, d2) {
  return Math.round((d2.getTime() - d1.getTime()) / 86400000);
}

function yearFraction(d1, d2, dayCount) {
  if (dayCount === '30/360')     return days30360(d1, d2) / 360;
  if (dayCount === 'actual/360') return daysActual(d1, d2) / 360;
  if (dayCount === 'actual/actual') {
    if (d2.getTime() <= d1.getTime()) return 0;
    const y1 = d1.getFullYear(), y2 = d2.getFullYear();
    if (y1 === y2) {
      const diy = ((y1 % 4 === 0 && y1 % 100 !== 0) || y1 % 400 === 0) ? 366 : 365;
      return daysActual(d1, d2) / diy;
    }
    let total = 0;
    const endY1 = new Date(y1 + 1, 0, 1);
    const diy1 = ((y1 % 4 === 0 && y1 % 100 !== 0) || y1 % 400 === 0) ? 366 : 365;
    total += daysActual(d1, endY1) / diy1;
    for (let y = y1 + 1; y < y2; y++) total += 1;
    const startY2 = new Date(y2, 0, 1);
    const diy2 = ((y2 % 4 === 0 && y2 % 100 !== 0) || y2 % 400 === 0) ? 366 : 365;
    total += daysActual(startY2, d2) / diy2;
    return total;
  }
  return days30360(d1, d2) / 360;
}

// ── Date helpers ─────────────────────────────────────────────
function parseDate(s) {
  if (!s) return null;
  const parts = String(s).split(/[-/]/);
  if (parts.length !== 3) return null;
  let m, d, y;
  if (parts[0].length === 4) { y = +parts[0]; m = +parts[1]; d = +parts[2]; }
  else                       { m = +parts[0]; d = +parts[1]; y = +parts[2]; }
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return isNaN(dt.getTime()) ? null : dt;
}

function fmtDate(dt) {
  if (!dt) return '';
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${dt.getFullYear()}`;
}

// ── IRR bisection solver — do NOT modify ────────────────────
function solveIRR(cashFlows, periodsPerYear, dayCount) {
  if (cashFlows.length < 2) return 0;
  const sorted = [...cashFlows].sort((a, b) => a.date.getTime() - b.date.getTime());
  const t0 = sorted[0].date;
  const n = periodsPerYear;

  function npv(r) {
    let total = 0;
    for (const cf of sorted) {
      const years = yearFraction(t0, cf.date, dayCount);
      const base = 1 + r / n;
      if (base <= 0) return Number.NaN;
      total += cf.amount * Math.pow(base, -n * years);
    }
    return total;
  }

  const scale = Math.max(1, ...sorted.map(cf => Math.abs(cf.amount)));
  const tol = scale * 1e-9;
  let lo = -0.50, hi = 2.00;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    const pvMid = npv(mid);
    if (!isFinite(pvMid)) { lo = mid; continue; }
    if (Math.abs(pvMid) < tol) return mid;
    if (pvMid > 0) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

// ── Styling (matches YieldCalculator.js) ─────────────────────
const sHdr = {
  background: 'var(--ribbon-bg)', color: 'var(--ribbon-text)',
  padding: '6px 14px', fontSize: '12px', fontWeight: 600,
  letterSpacing: '0.05em', textTransform: 'uppercase',
  borderRadius: '4px', marginBottom: '12px', marginTop: '22px',
};
const sSubHdr = {
  fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: '0.04em',
  marginTop: '14px', marginBottom: '6px',
};
const sInput = {
  width: '100%', padding: '7px 10px', border: '1px solid var(--border-color)',
  borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box', outline: 'none',
  background: 'var(--input-bg)', color: 'var(--text-primary)',
  fontFamily: 'var(--font-body)',
};
const sLabel = {
  display: 'block', fontSize: '11px', fontWeight: 600,
  color: 'var(--text-secondary)', marginBottom: '4px',
  textTransform: 'uppercase', letterSpacing: '0.04em',
};
const sBtn = {
  padding: '8px 18px', borderRadius: '4px', border: 'none', cursor: 'pointer',
  fontSize: '13px', fontWeight: 600, fontFamily: 'var(--font-body)',
  transition: 'var(--transition-fast)',
};
const sBtnPrimary = { ...sBtn, background: 'var(--accent-color)', color: '#fff' };
const sBtnSecondary = {
  ...sBtn, background: 'var(--bg-tertiary)', color: 'var(--text-primary)',
  border: '1px solid var(--border-color)',
};
const sBtnTiny = { ...sBtnSecondary, padding: '6px 12px', fontSize: '12px' };
const sTable = {
  width: '100%', borderCollapse: 'collapse', fontSize: '12px',
  fontFamily: 'var(--font-body)', marginTop: '8px',
};
const sTh = {
  padding: '6px 8px', textAlign: 'right', fontWeight: 600, fontSize: '11px',
  textTransform: 'uppercase', letterSpacing: '0.03em',
  color: 'var(--text-secondary)',
  borderBottom: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
};
const sTd = {
  padding: '5px 8px', textAlign: 'right',
  borderBottom: '1px solid var(--border-light)',
  fontFamily: "'DM Sans', monospace", fontSize: '12px',
};
const sFeeBox = {
  padding: '12px 14px', marginBottom: 10, borderRadius: 6,
  border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
};
const sChecklistLabel = {
  display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6,
  fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.4,
};
const sWarning = {
  marginTop: 10, padding: '10px 12px',
  background: 'rgba(230, 126, 34, 0.10)',
  border: '1px solid #e67e22', borderRadius: 4,
  color: '#c0572b', fontSize: 12, fontWeight: 500,
};

const fmt$ = (v) => v == null || isNaN(v) ? ''
  : (v < 0 ? '-$' : '$') + Math.abs(Number(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (v, dec = 4) => v == null || isNaN(v) ? '' : (v * 100).toFixed(dec) + '%';

// ── Constants ────────────────────────────────────────────────
const FREQUENCY_OPTIONS = [
  { value: 2,  label: 'Semi-annual' },
  { value: 1,  label: 'Annual' },
  { value: 4,  label: 'Quarterly' },
  { value: 12, label: 'Monthly' },
];

const DAY_COUNT_OPTIONS = [
  { value: '30/360',        label: '30/360' },
  { value: 'actual/360',    label: 'Actual/360' },
  { value: 'actual/actual', label: 'Actual/Actual' },
];

const TIMING_OPTIONS = [
  { value: 'upfront', label: 'Upfront (at loan origination, t=0)' },
  { value: 'custom',  label: 'Custom schedule (periodic/annual/ad-hoc)' },
];

const SPREAD_OPTIONS = [
  { key: 'program', label: 'Program investments — 1.50%',                            spread: 0.015   },
  { key: 'purpose', label: 'Purpose investments (not program investments) — 0.125%', spread: 0.00125 },
  { key: 'custom',  label: 'Custom spread',                                          spread: null    },
];

// Items 3, 4, 5 are the "disguised interest" tests — failing any auto-flags
// the fee as likely included in yield.
const QAC_CHECKLIST = [
  { id: 'q1', text: 'The fee is paid for actual administrative, monitoring, compliance, trustee, rebate, disclosure, servicing, or similar services, and not for the use of money.' },
  { id: 'q2', text: 'The fee is reasonable in amount relative to the services provided.' },
  { id: 'q3', text: 'The fee is NOT based on a percentage of loan balance, spread, yield, arbitrage earnings, cash flow, revenues, or net profits.' },
  { id: 'q4', text: 'The fee is NOT a charge for making or funding the loan (i.e., not a disguised upfront financing charge).' },
  { id: 'q5', text: 'The fee does NOT function as additional interest, discount, premium, or yield enhancement.' },
  { id: 'q6', text: 'If periodic, the fee is tied to actual ongoing services and not merely the passage of time on invested capital.' },
  { id: 'q7', text: 'There is support (documents or inputs) describing the services provided.' },
];
const DISGUISED_INTEREST_IDS = ['q3', 'q4', 'q5'];

const blankChecklist = () => ({ q1: false, q2: false, q3: false, q4: false, q5: false, q6: false, q7: false });

const blankFee = () => ({
  name: '', amount: '', timing: 'upfront',
  schedule: [], include_in_yield: true,
  checklist: blankChecklist(), override: false,
});

// ── Component ────────────────────────────────────────────────
const LoanYieldCalculator = () => {
  // Section A — conventions
  const [compoundingFreq, setCompoundingFreq] = useState(2);
  const [dayCount, setDayCount] = useState('30/360');

  // Section B — purpose investment cash flows
  const [initialDate, setInitialDate] = useState('');
  const [initialAmount, setInitialAmount] = useState('');
  const [draws, setDraws] = useState([]);
  const [interestPayments, setInterestPayments] = useState([]);
  const [principalRepayments, setPrincipalRepayments] = useState([]);
  const [otherReceipts, setOtherReceipts] = useState([]);

  // Section C — fees
  const [fees, setFees] = useState([]);

  // Section D — yield restriction
  const [bondYieldInput, setBondYieldInput] = useState('');
  const [spreadCategory, setSpreadCategory] = useState('purpose');
  const [customSpread, setCustomSpread] = useState('');

  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // ── Row helpers ────────────────────────────────────────────
  const addRow = (setter, template) => setter(prev => [...prev, { ...template }]);
  const removeRow = (setter, idx) => setter(prev => prev.filter((_, i) => i !== idx));
  const updateRow = (setter, idx, field, value) => {
    setter(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const parseAmount = (v) => parseFloat(String(v).replace(/[,$\s]/g, ''));

  // Fee helpers
  const addFee = () => setFees(prev => [...prev, blankFee()]);
  const removeFee = (idx) => setFees(prev => prev.filter((_, i) => i !== idx));
  const updateFee = (idx, patch) => setFees(prev => {
    const copy = [...prev];
    copy[idx] = { ...copy[idx], ...patch };
    return copy;
  });
  const updateFeeChecklist = (idx, qid, checked) => setFees(prev => {
    const copy = [...prev];
    copy[idx] = { ...copy[idx], checklist: { ...copy[idx].checklist, [qid]: checked } };
    return copy;
  });
  const updateFeeScheduleRow = (feeIdx, rowIdx, field, value) => setFees(prev => {
    const copy = [...prev];
    const sched = [...copy[feeIdx].schedule];
    sched[rowIdx] = { ...sched[rowIdx], [field]: value };
    copy[feeIdx] = { ...copy[feeIdx], schedule: sched };
    return copy;
  });
  const addFeeScheduleRow = (feeIdx) => setFees(prev => {
    const copy = [...prev];
    copy[feeIdx] = { ...copy[feeIdx], schedule: [...copy[feeIdx].schedule, { date: '', amount: '' }] };
    return copy;
  });
  const removeFeeScheduleRow = (feeIdx, rowIdx) => setFees(prev => {
    const copy = [...prev];
    copy[feeIdx] = { ...copy[feeIdx], schedule: copy[feeIdx].schedule.filter((_, i) => i !== rowIdx) };
    return copy;
  });
  const autofillAnnually = (feeIdx) => setFees(prev => {
    const copy = [...prev];
    const base = parseDate(initialDate);
    if (!base) return copy;
    const years = 10;
    const amount = copy[feeIdx].amount || '';
    const schedule = [];
    for (let k = 1; k <= years; k++) {
      const d = new Date(base);
      d.setFullYear(d.getFullYear() + k);
      schedule.push({ date: fmtDate(d), amount });
    }
    copy[feeIdx] = { ...copy[feeIdx], schedule };
    return copy;
  });

  // Auto-flag check (items q3/q4/q5 must all be TRUE to safely exclude)
  const feeIsAutoFlagged = (fee) =>
    DISGUISED_INTEREST_IDS.some(q => !fee.checklist[q]);

  // ── Main calculate ─────────────────────────────────────────
  const calculate = () => {
    setError('');
    setResult(null);
    try {
      // ── Validate initial loan ──────────────────────────
      if (!initialDate || !initialAmount) {
        throw new Error('Initial loan date and amount are required');
      }
      const initDt = parseDate(initialDate);
      if (!initDt) throw new Error('Invalid initial loan date');
      const initAmt = parseAmount(initialAmount);
      if (isNaN(initAmt) || initAmt <= 0) throw new Error('Invalid initial loan amount');

      // ── Validate bond yield + spread ───────────────────
      const bondYieldNum = parseFloat(bondYieldInput);
      if (isNaN(bondYieldNum)) {
        throw new Error('Bond yield is required for the yield restriction test');
      }
      const bondYield = bondYieldNum / 100;

      let spread;
      if (spreadCategory === 'custom') {
        const cs = parseFloat(customSpread);
        if (isNaN(cs)) throw new Error('Custom spread is required when "Custom" is selected');
        spread = cs / 100;
      } else {
        spread = SPREAD_OPTIONS.find(o => o.key === spreadCategory).spread;
      }
      const maxPermittedYield = bondYield + spread;

      // ── Fee classification gating ──────────────────────
      const feeAuditTrail = [];
      fees.forEach((f, i) => {
        if (!f.name && !f.amount && f.schedule.length === 0) return; // silently skip fully blank rows
        const amt = parseAmount(f.amount);
        if (isNaN(amt) || amt <= 0) {
          throw new Error(`Fee ${i + 1} (${f.name || 'unnamed'}): invalid amount`);
        }
        const autoFlagged = feeIsAutoFlagged(f);
        let treatment, explanation, warning = null;
        if (f.include_in_yield) {
          treatment = 'included';
          explanation = 'User classified as included in yield.';
        } else {
          // Excluding requires checklist confirmation
          if (autoFlagged && !f.override) {
            throw new Error(
              `Fee ${i + 1} (${f.name || 'unnamed'}) cannot be excluded: QAC checklist item(s) ` +
              `${DISGUISED_INTEREST_IDS.filter(q => !f.checklist[q]).join(', ')} not confirmed. ` +
              `Check the "override" box to force exclusion.`
            );
          }
          treatment = 'excluded';
          explanation = 'Excluded as qualified administrative cost under Treas. Reg. §1.148-5(e).';
          if (autoFlagged && f.override) {
            warning = 'Excluded via override — this fee may be required to be included in yield under §1.148-5(e) because it failed one or more of the "disguised interest" checklist items (q3, q4, q5).';
          }
        }

        feeAuditTrail.push({
          idx: i + 1,
          name: f.name || `Fee ${i + 1}`,
          amount: amt,
          timing: f.timing,
          scheduleCount: f.schedule.length,
          include_in_yield: f.include_in_yield,
          autoFlagged,
          override: f.override,
          checklist: { ...f.checklist },
          treatment,
          explanation,
          warning,
        });
      });

      // ── Build cash flows ───────────────────────────────
      const cashFlows = [];

      // Initial loan (money out from lender)
      cashFlows.push({
        date: initDt, amount: -initAmt,
        description: 'Initial loan advance', section: 'Principal out',
      });

      draws.forEach((d, i) => {
        if (!d.date && !d.amount) return;
        const dt = parseDate(d.date);
        const amt = parseAmount(d.amount);
        if (!dt) throw new Error(`Additional draw ${i + 1}: invalid date`);
        if (isNaN(amt) || amt <= 0) throw new Error(`Additional draw ${i + 1}: invalid amount`);
        cashFlows.push({
          date: dt, amount: -amt,
          description: 'Additional draw', section: 'Principal out',
        });
      });

      interestPayments.forEach((p, i) => {
        if (!p.date && !p.amount) return;
        const dt = parseDate(p.date);
        const amt = parseAmount(p.amount);
        if (!dt) throw new Error(`Interest payment ${i + 1}: invalid date`);
        if (isNaN(amt) || amt <= 0) throw new Error(`Interest payment ${i + 1}: invalid amount`);
        cashFlows.push({
          date: dt, amount: amt,
          description: 'Interest payment', section: 'Interest in',
        });
      });

      principalRepayments.forEach((p, i) => {
        if (!p.date && !p.amount) return;
        const dt = parseDate(p.date);
        const amt = parseAmount(p.amount);
        if (!dt) throw new Error(`Principal repayment ${i + 1}: invalid date`);
        if (isNaN(amt) || amt <= 0) throw new Error(`Principal repayment ${i + 1}: invalid amount`);
        cashFlows.push({
          date: dt, amount: amt,
          description: 'Principal repayment', section: 'Principal in',
        });
      });

      otherReceipts.forEach((r, i) => {
        if (!r.date && !r.amount) return;
        const dt = parseDate(r.date);
        const amt = parseAmount(r.amount);
        if (!dt) throw new Error(`Other receipt ${i + 1}: invalid date`);
        if (isNaN(amt) || amt <= 0) throw new Error(`Other receipt ${i + 1}: invalid amount`);
        cashFlows.push({
          date: dt, amount: amt,
          description: r.description || 'Other receipt', section: 'Other in',
        });
      });

      // Included fees → receipts to the lender
      // Upfront = at initial loan date; custom = at each scheduled date
      let totalIncludedUpfrontFees = 0;
      let totalIncludedPeriodicFees = 0;
      fees.forEach((f, i) => {
        if (!f.include_in_yield) return; // excluded — do not touch cash flows
        const amt = parseAmount(f.amount);
        if (isNaN(amt) || amt <= 0) return;
        if (f.timing === 'upfront') {
          cashFlows.push({
            date: initDt, amount: amt,
            description: `${f.name || `Fee ${i + 1}`} (upfront)`,
            section: 'Fee (incl.)',
          });
          totalIncludedUpfrontFees += amt;
        } else {
          // custom: each schedule row is its own receipt
          f.schedule.forEach((row, j) => {
            if (!row.date && !row.amount) return;
            const dt = parseDate(row.date);
            const rowAmt = parseAmount(row.amount);
            if (!dt) throw new Error(`Fee ${i + 1} schedule row ${j + 1}: invalid date`);
            if (isNaN(rowAmt) || rowAmt <= 0) throw new Error(`Fee ${i + 1} schedule row ${j + 1}: invalid amount`);
            cashFlows.push({
              date: dt, amount: rowAmt,
              description: `${f.name || `Fee ${i + 1}`} (scheduled)`,
              section: 'Fee (incl.)',
            });
            totalIncludedPeriodicFees += rowAmt;
          });
        }
      });

      // ── Sanity on cash flows ───────────────────────────
      if (cashFlows.length < 2) {
        throw new Error('Need at least two cash flows to compute a yield');
      }
      const hasNeg = cashFlows.some(cf => cf.amount < 0);
      const hasPos = cashFlows.some(cf => cf.amount > 0);
      if (!hasNeg || !hasPos) {
        throw new Error('Need at least one payment (money out) and one receipt (money in)');
      }

      // ── Solve IRR ──────────────────────────────────────
      const investmentYield = solveIRR(cashFlows, compoundingFreq, dayCount);

      // ── Build schedule with PV column ──────────────────
      const sorted = [...cashFlows].sort((a, b) => a.date.getTime() - b.date.getTime());
      const t0 = sorted[0].date;
      const schedule = sorted.map(cf => {
        const years = yearFraction(t0, cf.date, dayCount);
        const base = 1 + investmentYield / compoundingFreq;
        const factor = Math.pow(base, -compoundingFreq * years);
        return { ...cf, years, factor, pv: cf.amount * factor };
      });

      // ── Yield restriction test ─────────────────────────
      const actualSpread = investmentYield - bondYield;
      const restrictionPass = investmentYield <= maxPermittedYield + 1e-12; // tiny numerical slack

      // ── Adjusted net advance ───────────────────────────
      const adjustedNetAdvance = initAmt - totalIncludedUpfrontFees;

      setResult({
        investmentYield,
        compoundingFreq,
        dayCount,
        bondYield,
        spread,
        spreadCategory,
        maxPermittedYield,
        actualSpread,
        restrictionPass,
        schedule,
        feeAuditTrail,
        grossInitial: initAmt,
        totalIncludedUpfrontFees,
        totalIncludedPeriodicFees,
        adjustedNetAdvance,
        includedFees: feeAuditTrail.filter(f => f.treatment === 'included'),
        excludedFees: feeAuditTrail.filter(f => f.treatment === 'excluded'),
        flaggedFees:  feeAuditTrail.filter(f => f.warning),
      });
    } catch (e) {
      setError(e.message);
    }
  };

  const resetAll = () => {
    setCompoundingFreq(2);
    setDayCount('30/360');
    setInitialDate('');
    setInitialAmount('');
    setDraws([]);
    setInterestPayments([]);
    setPrincipalRepayments([]);
    setOtherReceipts([]);
    setFees([]);
    setBondYieldInput('');
    setSpreadCategory('purpose');
    setCustomSpread('');
    setResult(null);
    setError('');
  };

  // ── Render helpers ─────────────────────────────────────────
  const renderDateAmountRows = (rows, setter, addLabel, placeholderLabel) => (
    <>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
          <input
            type="text" placeholder="MM/DD/YYYY" value={r.date}
            onChange={e => updateRow(setter, i, 'date', e.target.value)}
            style={{ ...sInput, flex: '0 0 160px' }}
          />
          <input
            type="text" placeholder={placeholderLabel || 'Amount'} value={r.amount}
            onChange={e => updateRow(setter, i, 'amount', e.target.value)}
            style={{ ...sInput, flex: 1 }}
          />
          <button style={sBtnTiny} onClick={() => removeRow(setter, i)}>Remove</button>
        </div>
      ))}
      <button style={sBtnSecondary} onClick={() => addRow(setter, { date: '', amount: '' })}>
        + {addLabel}
      </button>
    </>
  );

  const currentSpreadPct = (() => {
    if (spreadCategory === 'custom') {
      const v = parseFloat(customSpread);
      return isNaN(v) ? '—' : `${v.toFixed(4)}%`;
    }
    const opt = SPREAD_OPTIONS.find(o => o.key === spreadCategory);
    return opt ? `${(opt.spread * 100).toFixed(4)}%` : '—';
  })();

  // ── Render ─────────────────────────────────────────────────
  return (
    <div style={{
      flex: 1, overflowY: 'auto', padding: '24px 32px',
      fontFamily: 'var(--font-body)', maxWidth: '1000px',
      margin: '0 auto', width: '100%',
    }}>
      <h2 style={{
        fontFamily: 'var(--font-reading)', fontSize: '22px', fontWeight: 600,
        color: 'var(--text-primary)', marginBottom: '4px',
      }}>
        Loan Yield Calculator
      </h2>
      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
        Computes purpose-investment yield per Treas. Reg. §1.148-5 as an IRR over the
        stream of unconditionally payable amounts, with fee classification and
        permissible-spread testing.
      </p>

      {/* ── Section A — Conventions ─────────────────────── */}
      <div style={sHdr}>A. Conventions</div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 220px', minWidth: 200 }}>
          <label style={sLabel}>Compounding Frequency</label>
          <select
            value={compoundingFreq}
            onChange={e => setCompoundingFreq(parseInt(e.target.value, 10))}
            style={sInput}
          >
            {FREQUENCY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: '1 1 220px', minWidth: 200 }}>
          <label style={sLabel}>Day Count</label>
          <select value={dayCount} onChange={e => setDayCount(e.target.value)} style={sInput}>
            {DAY_COUNT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Section B — Purpose Investment Cash Flows ──── */}
      <div style={sHdr}>B. Purpose Investment Cash Flows</div>

      <div style={{ marginBottom: 16 }}>
        <label style={sLabel}>Initial Loan Advance</label>
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            type="text" placeholder="MM/DD/YYYY"
            value={initialDate} onChange={e => setInitialDate(e.target.value)}
            style={{ ...sInput, flex: '0 0 160px' }}
          />
          <input
            type="text" placeholder="Amount"
            value={initialAmount} onChange={e => setInitialAmount(e.target.value)}
            style={{ ...sInput, flex: 1 }}
          />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={sLabel}>Additional Draws</label>
        {renderDateAmountRows(draws, setDraws, 'Add Draw')}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={sLabel}>Interest Payments</label>
        {renderDateAmountRows(interestPayments, setInterestPayments, 'Add Interest Payment')}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={sLabel}>Principal Repayments</label>
        {renderDateAmountRows(principalRepayments, setPrincipalRepayments, 'Add Principal Repayment')}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={sLabel}>Other Receipts</label>
        {otherReceipts.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
            <input type="text" placeholder="MM/DD/YYYY" value={r.date}
              onChange={e => updateRow(setOtherReceipts, i, 'date', e.target.value)}
              style={{ ...sInput, flex: '0 0 150px' }} />
            <input type="text" placeholder="Amount" value={r.amount}
              onChange={e => updateRow(setOtherReceipts, i, 'amount', e.target.value)}
              style={{ ...sInput, flex: '0 0 140px' }} />
            <input type="text" placeholder="Description" value={r.description}
              onChange={e => updateRow(setOtherReceipts, i, 'description', e.target.value)}
              style={{ ...sInput, flex: 1 }} />
            <button style={sBtnTiny} onClick={() => removeRow(setOtherReceipts, i)}>Remove</button>
          </div>
        ))}
        <button style={sBtnSecondary}
          onClick={() => addRow(setOtherReceipts, { date: '', amount: '', description: '' })}
        >
          + Add Receipt
        </button>
      </div>

      {/* ── Section C — Fees & Classification ───────────── */}
      <div style={sHdr}>C. Fees & §1.148-5(e) Classification</div>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
        Every fee must be classified as either <strong>included in yield</strong> or
        <strong> excluded as a qualified administrative cost (QAC)</strong>. Excluding a
        fee requires completing the QAC checklist. Items 3–5 of the checklist test for
        "disguised interest"; failing any of them auto-flags the fee as likely included.
      </p>

      {fees.map((fee, fIdx) => {
        const flagged = feeIsAutoFlagged(fee);
        const blockingExclusion = !fee.include_in_yield && flagged && !fee.override;
        return (
          <div key={fIdx} style={sFeeBox}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 200px', minWidth: 180 }}>
                <label style={sLabel}>Fee Name</label>
                <input type="text" placeholder="e.g., Origination Fee"
                  value={fee.name}
                  onChange={e => updateFee(fIdx, { name: e.target.value })}
                  style={sInput} />
              </div>
              <div style={{ flex: '0 0 160px' }}>
                <label style={sLabel}>Amount</label>
                <input type="text" placeholder="Amount" value={fee.amount}
                  onChange={e => updateFee(fIdx, { amount: e.target.value })}
                  style={sInput} />
              </div>
              <div style={{ flex: '1 1 260px', minWidth: 240 }}>
                <label style={sLabel}>Timing</label>
                <select value={fee.timing}
                  onChange={e => updateFee(fIdx, { timing: e.target.value })}
                  style={sInput}>
                  {TIMING_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button style={sBtnTiny} onClick={() => removeFee(fIdx)}>Remove Fee</button>
              </div>
            </div>

            {fee.timing === 'custom' && (
              <div style={{ marginBottom: 10 }}>
                <div style={sSubHdr}>Schedule</div>
                {fee.schedule.map((row, rIdx) => (
                  <div key={rIdx} style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
                    <input type="text" placeholder="MM/DD/YYYY" value={row.date}
                      onChange={e => updateFeeScheduleRow(fIdx, rIdx, 'date', e.target.value)}
                      style={{ ...sInput, flex: '0 0 160px' }} />
                    <input type="text" placeholder="Amount" value={row.amount}
                      onChange={e => updateFeeScheduleRow(fIdx, rIdx, 'amount', e.target.value)}
                      style={{ ...sInput, flex: 1 }} />
                    <button style={sBtnTiny} onClick={() => removeFeeScheduleRow(fIdx, rIdx)}>Remove</button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={sBtnSecondary} onClick={() => addFeeScheduleRow(fIdx)}>+ Add Date</button>
                  <button style={sBtnSecondary} onClick={() => autofillAnnually(fIdx)}
                    title="Fill 10 annual anniversaries of the initial loan date using the amount above">
                    Auto-fill Annually (10 yr)
                  </button>
                </div>
              </div>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 6, marginBottom: 6 }}>
              <input type="checkbox" checked={fee.include_in_yield}
                onChange={e => updateFee(fIdx, { include_in_yield: e.target.checked })} />
              <span><strong>Include in yield</strong> (uncheck to back out as qualified administrative cost)</span>
            </label>

            {!fee.include_in_yield && (
              <div style={{
                marginTop: 8, padding: '10px 12px',
                border: '1px solid var(--border-color)',
                borderRadius: 4, background: 'var(--bg-primary)',
              }}>
                <div style={sSubHdr}>Qualified Administrative Cost Checklist</div>
                {QAC_CHECKLIST.map(item => (
                  <label key={item.id} style={sChecklistLabel}>
                    <input type="checkbox" checked={!!fee.checklist[item.id]}
                      onChange={e => updateFeeChecklist(fIdx, item.id, e.target.checked)}
                      style={{ marginTop: 2 }} />
                    <span>
                      <strong>{item.id.toUpperCase()}.</strong> {item.text}
                      {DISGUISED_INTEREST_IDS.includes(item.id) && (
                        <span style={{ color: '#c0572b', fontWeight: 600 }}> (disguised-interest test)</span>
                      )}
                    </span>
                  </label>
                ))}
                {flagged && (
                  <div style={sWarning}>
                    ⚠ This fee has failed at least one "disguised interest" item
                    (q3, q4, or q5) and may be required to be <strong>included in
                    yield</strong> under Treas. Reg. §1.148-5(e). Confirm override to
                    exclude anyway:
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                      <input type="checkbox" checked={fee.override}
                        onChange={e => updateFee(fIdx, { override: e.target.checked })} />
                      <span>Override — exclude anyway (document rationale in memo file).</span>
                    </label>
                  </div>
                )}
                {blockingExclusion && (
                  <div style={{ ...sWarning, background: 'rgba(211,47,47,0.10)', borderColor: '#d32f2f', color: '#b3261e' }}>
                    Cannot exclude: check the override box above, or classify this fee as included in yield.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      <button style={sBtnSecondary} onClick={addFee}>+ Add Fee</button>

      {/* ── Section D — Yield Restriction ───────────────── */}
      <div style={sHdr}>D. Yield Restriction</div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ flex: '1 1 220px', minWidth: 200 }}>
          <label style={sLabel}>Bond Yield (%)</label>
          <input type="text" placeholder="e.g., 3.1234"
            value={bondYieldInput}
            onChange={e => setBondYieldInput(e.target.value)}
            style={sInput} />
        </div>
        <div style={{ flex: '1 1 300px', minWidth: 280 }}>
          <label style={sLabel}>Permissible Yield Spread</label>
          <select value={spreadCategory}
            onChange={e => setSpreadCategory(e.target.value)}
            style={sInput}>
            {SPREAD_OPTIONS.map(opt => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
        </div>
        {spreadCategory === 'custom' && (
          <div style={{ flex: '1 1 220px', minWidth: 200 }}>
            <label style={sLabel}>Custom Spread (%)</label>
            <input type="text" placeholder="e.g., 0.25"
              value={customSpread}
              onChange={e => setCustomSpread(e.target.value)}
              style={sInput} />
          </div>
        )}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        Currently selected spread: <strong>{currentSpreadPct}</strong>
      </div>

      {/* ── Actions ───────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 22, marginBottom: 16 }}>
        <button style={sBtnPrimary} onClick={calculate}>Calculate Yield</button>
        <button style={sBtnSecondary} onClick={resetAll}>Clear / Reset</button>
      </div>

      {error && (
        <div style={{
          color: '#b3261e', padding: '10px 14px', marginBottom: 16,
          border: '1px solid #d32f2f', borderRadius: 4,
          background: 'rgba(211, 47, 47, 0.08)', fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {/* ── Section E — Results ──────────────────────────── */}
      {result && (
        <>
          <div style={sHdr}>E. Results</div>

          {/* Yield card */}
          <div style={{
            padding: '16px 20px', marginBottom: 14,
            background: 'var(--bg-secondary)', borderRadius: 6,
            border: '1px solid var(--border-color)',
          }}>
            <div style={{
              fontSize: 11, fontWeight: 600, letterSpacing: '0.05em',
              textTransform: 'uppercase', color: 'var(--text-secondary)',
            }}>
              Computed Purpose Investment Yield
            </div>
            <div style={{
              fontSize: 30, fontWeight: 600, marginTop: 4,
              color: 'var(--accent-color)', fontFamily: "'DM Sans', monospace",
            }}>
              {fmtPct(result.investmentYield, 4)}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              Compounding:&nbsp;
              <strong>{FREQUENCY_OPTIONS.find(f => f.value === result.compoundingFreq)?.label}</strong>
              &nbsp;·&nbsp;Day count:&nbsp;<strong>{result.dayCount}</strong>
            </div>
          </div>

          {/* Yield restriction PASS/FAIL */}
          <div style={{
            padding: '14px 18px', marginBottom: 14, borderRadius: 6,
            background: result.restrictionPass ? 'rgba(46, 125, 50, 0.10)' : 'rgba(211, 47, 47, 0.10)',
            border: `1px solid ${result.restrictionPass ? '#2e7d32' : '#d32f2f'}`,
          }}>
            <div style={{
              fontSize: 11, fontWeight: 600, letterSpacing: '0.05em',
              textTransform: 'uppercase', color: 'var(--text-secondary)',
              marginBottom: 8,
            }}>
              Yield Restriction Analysis (§1.148-5)
            </div>
            <table style={{ width: '100%', fontSize: 13, fontFamily: "'DM Sans', monospace" }}>
              <tbody>
                <tr><td>Bond yield</td><td style={{ textAlign: 'right' }}><strong>{fmtPct(result.bondYield, 4)}</strong></td></tr>
                <tr><td>Permissible spread ({result.spreadCategory})</td><td style={{ textAlign: 'right' }}><strong>{fmtPct(result.spread, 4)}</strong></td></tr>
                <tr><td>Maximum permitted yield</td><td style={{ textAlign: 'right' }}><strong>{fmtPct(result.maxPermittedYield, 4)}</strong></td></tr>
                <tr><td>Computed investment yield</td><td style={{ textAlign: 'right' }}><strong>{fmtPct(result.investmentYield, 4)}</strong></td></tr>
                <tr><td>Actual spread (investment − bond)</td><td style={{ textAlign: 'right' }}><strong>{fmtPct(result.actualSpread, 4)}</strong></td></tr>
              </tbody>
            </table>
            <div style={{
              marginTop: 10, fontWeight: 700, fontSize: 14,
              color: result.restrictionPass ? '#2e7d32' : '#b3261e',
            }}>
              {result.restrictionPass
                ? '✓ PASS — no yield restriction violation'
                : '✗ FAIL — yield restriction violated'}
            </div>
          </div>

          {/* Classification summary */}
          <div style={{
            padding: '14px 18px', marginBottom: 14, borderRadius: 6,
            border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
            fontSize: 13,
          }}>
            <div style={{
              fontSize: 11, fontWeight: 600, letterSpacing: '0.05em',
              textTransform: 'uppercase', color: 'var(--text-secondary)',
              marginBottom: 8,
            }}>
              Yield Calculation Summary
            </div>
            <table style={{ width: '100%', fontSize: 12, fontFamily: "'DM Sans', monospace" }}>
              <tbody>
                <tr><td>Gross initial loan advance</td><td style={{ textAlign: 'right' }}>{fmt$(result.grossInitial)}</td></tr>
                <tr><td>Total upfront included fees</td><td style={{ textAlign: 'right' }}>{fmt$(result.totalIncludedUpfrontFees)}</td></tr>
                <tr><td><strong>Adjusted net advance</strong></td><td style={{ textAlign: 'right' }}><strong>{fmt$(result.adjustedNetAdvance)}</strong></td></tr>
                <tr><td>Total periodic included fees</td><td style={{ textAlign: 'right' }}>{fmt$(result.totalIncludedPeriodicFees)}</td></tr>
                <tr><td>Final computed purpose-investment yield</td><td style={{ textAlign: 'right' }}><strong>{fmtPct(result.investmentYield, 4)}</strong></td></tr>
              </tbody>
            </table>
            {result.includedFees.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={sSubHdr}>Included Fees</div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                  {result.includedFees.map((f, i) => (
                    <li key={i}>{f.name} — {fmt$(f.amount)} ({f.timing})</li>
                  ))}
                </ul>
              </div>
            )}
            {result.excludedFees.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={sSubHdr}>Excluded (Qualified Administrative Cost) Fees</div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                  {result.excludedFees.map((f, i) => (
                    <li key={i}>
                      {f.name} — {fmt$(f.amount)} ({f.timing})
                      {f.warning && <span style={{ color: '#c0572b' }}> — FLAGGED</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.flaggedFees.length > 0 && (
              <div style={{ ...sWarning, marginTop: 10 }}>
                {result.flaggedFees.length} fee(s) excluded via override despite failing the disguised-interest
                tests (q3/q4/q5). Document the rationale in your working papers.
              </div>
            )}
          </div>

          {/* Fee audit trail */}
          {result.feeAuditTrail.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={sSubHdr}>Fee-Level Audit Trail</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={sTable}>
                  <thead>
                    <tr>
                      <th style={{ ...sTh, textAlign: 'left' }}>#</th>
                      <th style={{ ...sTh, textAlign: 'left' }}>Name</th>
                      <th style={sTh}>Amount</th>
                      <th style={{ ...sTh, textAlign: 'left' }}>Timing</th>
                      <th style={{ ...sTh, textAlign: 'left' }}>Classification</th>
                      <th style={{ ...sTh, textAlign: 'left' }}>Auto-flag</th>
                      <th style={{ ...sTh, textAlign: 'left' }}>Override</th>
                      <th style={{ ...sTh, textAlign: 'left' }}>Treatment</th>
                      <th style={{ ...sTh, textAlign: 'left' }}>Explanation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.feeAuditTrail.map((f, i) => (
                      <tr key={i}>
                        <td style={{ ...sTd, textAlign: 'left' }}>{f.idx}</td>
                        <td style={{ ...sTd, textAlign: 'left' }}>{f.name}</td>
                        <td style={sTd}>{fmt$(f.amount)}</td>
                        <td style={{ ...sTd, textAlign: 'left' }}>{f.timing}{f.timing === 'custom' ? ` (${f.scheduleCount})` : ''}</td>
                        <td style={{ ...sTd, textAlign: 'left' }}>{f.include_in_yield ? 'Include in yield' : 'Exclude as QAC'}</td>
                        <td style={{ ...sTd, textAlign: 'left' }}>{f.autoFlagged ? 'yes' : 'no'}</td>
                        <td style={{ ...sTd, textAlign: 'left' }}>{f.override ? 'yes' : ''}</td>
                        <td style={{ ...sTd, textAlign: 'left', color: f.treatment === 'included' ? '#2e7d32' : '#c0572b', fontWeight: 600 }}>
                          {f.treatment}
                        </td>
                        <td style={{ ...sTd, textAlign: 'left', fontSize: 11 }}>{f.explanation}{f.warning ? ` — ${f.warning}` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Cash flow schedule */}
          <div style={{ marginBottom: 14 }}>
            <div style={sSubHdr}>Cash Flow Schedule</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={sTable}>
                <thead>
                  <tr>
                    <th style={{ ...sTh, textAlign: 'left' }}>Date</th>
                    <th style={{ ...sTh, textAlign: 'left' }}>Description</th>
                    <th style={{ ...sTh, textAlign: 'left' }}>Section</th>
                    <th style={sTh}>Amount</th>
                    <th style={sTh}>Years</th>
                    <th style={sTh}>Discount Factor</th>
                    <th style={sTh}>Present Value</th>
                  </tr>
                </thead>
                <tbody>
                  {result.schedule.map((cf, i) => (
                    <tr key={i}>
                      <td style={{ ...sTd, textAlign: 'left' }}>{fmtDate(cf.date)}</td>
                      <td style={{ ...sTd, textAlign: 'left' }}>{cf.description}</td>
                      <td style={{ ...sTd, textAlign: 'left' }}>{cf.section}</td>
                      <td style={{ ...sTd, color: cf.amount < 0 ? '#c62828' : '#2e7d32' }}>{fmt$(cf.amount)}</td>
                      <td style={sTd}>{cf.years.toFixed(4)}</td>
                      <td style={sTd}>{cf.factor.toFixed(8)}</td>
                      <td style={sTd}>{fmt$(cf.pv)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{
            padding: '12px 14px', fontSize: 12,
            color: 'var(--text-secondary)', fontStyle: 'italic',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)', borderRadius: 4,
          }}>
            Yield computed under Treas. Reg. §1.148-5. Purpose investments are valued
            at present value, not fair market value. All unconditionally payable
            amounts are included; qualified administrative costs are excluded where
            the §1.148-5(e) checklist is satisfied. The solver is unchanged from the
            Bond Yield Calculator; only the cash-flow construction layer differs.
          </div>
        </>
      )}
    </div>
  );
};

export default LoanYieldCalculator;
